import { useEffect, useState } from "react";
import { Check, Loader2, Pencil } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { ScoreBar, TONE_TEXT } from "../components/mobile/ScorecardStrip";
import { ListRow, Section, RowAction } from "../components/mobile/ListRow";
import { useT, locale } from "../lib/i18n";

function fmtRange(w) {
  const f = (d) => new Date(d).toLocaleDateString(locale(), { day: "numeric", month: "short" });
  return `${f(w.week_start)} – ${f(w.week_end)}`;
}

export default function ProgramsPage() {
  const t = useT();
  const [week, setWeek] = useState(null);
  const [history, setHistory] = useState([]);
  const [targets, setTargets] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [w, h, t] = await Promise.all([api.get("/scorecard/week"), api.get("/scorecard/history?weeks=8"), api.get("/scorecard/targets")]);
    setWeek(w.data); setHistory(h.data || []); setTargets(t.data);
  };
  useEffect(() => { load(); }, []);

  const saveTargets = async () => {
    setSaving(true);
    try {
      await api.put("/scorecard/targets", { contacts: Number(targets.contacts), appointments: Number(targets.appointments), tasks: Number(targets.tasks) });
      setEditing(false); await load();
    } finally { setSaving(false); }
  };

  return (
    <Layout title={t("Programma's")} subtitle={t("Weekscore")} compact mobileFlush
      right={<button onClick={() => setEditing((v) => !v)} className="w-10 h-10 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center active:scale-95" data-testid="programs-edit-targets" aria-label={t("Doelen aanpassen")}><Pencil size={15} /></button>}>
      <div className="max-w-xl lg:max-w-3xl" data-testid="programs-page">
        {!week && <div className="px-4 py-6 text-sm text-slate-400">{t("Laden…")}</div>}
        {week && (
          <>
            <div className="px-4 pt-5 pb-2" data-testid="programs-overall">
              <div className="text-xs text-slate-500">{t("Week")} {week.iso_week} · {fmtRange(week)} · {t("dag")} {week.days_elapsed}/7</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className={`text-4xl font-bold tracking-tight tabular-nums ${TONE_TEXT[week.overall_tone]}`} data-testid="programs-overall-pct">{week.overall_pct}%</span>
                <span className="text-sm text-slate-500">{t("van je weekdoelen")}</span>
              </div>
              <ScoreBar pct={week.overall_pct} tone={week.overall_tone} className="mt-3 h-2" />
            </div>

            <Section title={t("Deze week")} testid="programs-metrics">
              {week.metrics.map((m) => (
                <div key={m.key} className="px-4 py-3 border-b border-slate-100" data-testid={`programs-metric-${m.key}`}>
                  <div className="flex items-baseline justify-between">
                    <div className="text-sm font-medium text-slate-900">{t(m.label)}</div>
                    <div className="text-sm tabular-nums"><span className={`font-semibold ${TONE_TEXT[m.tone]}`}>{m.value}</span><span className="text-slate-400"> / </span>
                      {editing ? (
                        <input type="number" min="1" className="pm-input h-8 w-16 inline-block text-sm" value={targets[m.key]} onChange={(e) => setTargets((t) => ({ ...t, [m.key]: e.target.value }))} data-testid={`programs-target-${m.key}`} />
                      ) : <span className="text-slate-500">{m.target}</span>}
                    </div>
                  </div>
                  <ScoreBar pct={m.pct} tone={m.tone} className="mt-2" />
                  <div className="text-xs text-slate-500 mt-1.5">
                    {m.key === "contacts" && t("{a} berichten · {b} notities", { a: m.breakdown.messages, b: m.breakdown.notes })}
                    {m.key === "appointments" && t("{a} gehad · {b} gepland deze week", { a: m.value, b: m.planned })}
                    {m.key === "tasks" && t("{a}% van doel", { a: m.pct })}
                  </div>
                </div>
              ))}
              {editing && (
                <div className="px-4 py-3 flex justify-end">
                  <RowAction primary onClick={saveTargets} testid="programs-save-targets">{saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {t("Doelen opslaan")}</RowAction>
                </div>
              )}
            </Section>

            <Section title={t("Vorige weken")} testid="programs-history">
              {history.slice(1).map((w) => (
                <ListRow key={w.week_start} tone={w.overall_tone} leading={`wk ${w.iso_week}`} title={fmtRange(w)}
                  meta={w.metrics.map((m) => `${t(m.label).split(" ")[0]} ${m.value}/${m.target}`).join(" · ")}
                  testid={`programs-week-${w.iso_week}`}
                  actions={<span className={`text-sm font-semibold tabular-nums pr-1 ${TONE_TEXT[w.overall_tone]}`}>{w.overall_pct}%</span>} />
              ))}
            </Section>
            <div className="px-4 py-4 text-xs text-slate-400">{t("Rood 0–60 % · Oranje 60–84 % · Groen 85 %+")}</div>
          </>
        )}
      </div>
    </Layout>
  );
}
